import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { startOfMonth, formatISO, addMonths } from 'date-fns';
import axios from '../../plugins/axios';
import type { AppDispatch, RootState } from '../index';

const apiUrl = process.env.REACT_APP_WORKNEST_TOOLS_API_URL;

interface Target {
    targetId: number | string;
    pillarId: number | string;
    areaId: number;
    targetType: number;
    childTargetIds?: (number | string)[];
    rollupTargetId?: number | string | null;
    [k: string]: any;
}
interface Pillar { id: number | string; name: string; [k: string]: any }
interface Area { id: number; name: string; [k: string]: any }

interface TargetState {
    pillarId: number | string | null;
    areaId: number;
    loading: boolean;
    targets: Target[];
    pillars: Pillar[];
    areas: Area[];
    selectedDate: Date;
    view: 'balanced' | 'scorecard' | 'scorecard2';
    editingTarget: any | null;
    editingType: 'details' | 'limits' | 'actual' | null;
}

const initialState: TargetState = {
    pillarId: null,
    areaId: 0,
    loading: false,
    targets: [],
    pillars: [],
    areas: [],
    selectedDate: addMonths(startOfMonth(new Date()), -1),
    view: 'balanced',
    editingTarget: null,
    editingType: null,
};

const targetSlice = createSlice({
    name: 'target',
    initialState,
    reducers: {
        setPillarId(state, action: PayloadAction<number | string | null>) {
            state.pillarId = action.payload;
            state.view = 'scorecard';
        },
        setAreaId(state, action: PayloadAction<number>) {
            state.areaId = action.payload;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        setPillars(state, action: PayloadAction<Pillar[]>) {
            state.pillars = action.payload;
            state.pillarId = action.payload[0]?.id ?? null;
        },
        setAreas(state, action: PayloadAction<Area[]>) {
            state.areas = action.payload;
        },
        setTargets(state, action: PayloadAction<Target[]>) {
            state.loading = false;
            state.targets = action.payload;
        },
        setSelectedDate(state, action: PayloadAction<Date | string>) {
            state.selectedDate = (typeof action.payload === 'string' ? new Date(action.payload) : action.payload);
        },
        setView(state, action: PayloadAction<TargetState['view']>) {
            state.view = action.payload;
        },
        setEditing(state, action: PayloadAction<{ targetId: number | string | null; editingType: TargetState['editingType'] }>) {
            const { targetId, editingType } = action.payload;

            const editingTarget = targetId === null
                ? { targetType: 0 }
                : state.targets.find((x) => x.targetId === targetId);

            const childTargetIds =
                (editingTarget as Target | undefined)?.childTargetIds
                ?? state.targets
                    .filter((t) => t.rollupTargetId === (editingTarget as Target | undefined)?.targetId)
                    .map((t) => t.targetId);

            state.editingTarget = { ...editingTarget, childTargetIds };
            state.editingType = editingType;
        },
        setTargetValue(state, action: PayloadAction<{ field: string; value: unknown }>) {
            if (!state.editingTarget) return;
            state.editingTarget[action.payload.field] = action.payload.value;
        },
    },
});

export const {
    setPillarId,
    setAreaId,
    setLoading,
    setPillars,
    setAreas,
    setTargets,
    setSelectedDate,
    setView,
    setEditing,
    setTargetValue,
} = targetSlice.actions;

// ---- Orchestration thunks (plain functions; thunk middleware wired by default).
const fmtDate = (d: Date) => formatISO(d, { representation: 'date' });

export const getTargets = () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const { targetReducer } = getState();
    const formattedDate = fmtDate(targetReducer.selectedDate);

    dispatch(setLoading(true));

    const response = await axios.get(`${apiUrl}/gettargets/${formattedDate}`);
    dispatch(setPillars(response.data.pillars));
    dispatch(setAreas(response.data.areas));
    dispatch(setTargets(response.data.targets));
};

export const completeAction = (targetId: number | string) => async (
    dispatch: AppDispatch,
    getState: () => RootState,
) => {
    const { targetReducer } = getState();
    const formattedDate = fmtDate(targetReducer.selectedDate);

    dispatch(setLoading(true));
    await axios.get(`${apiUrl}/completeAction/${formattedDate}/${targetId}`);
    dispatch(getTargets());
};

export const saveTarget = () => async (
    dispatch: AppDispatch,
    getState: () => RootState,
) => {
    dispatch(setLoading(true));

    const { targetReducer } = getState();
    const formattedDate = fmtDate(targetReducer.selectedDate);

    try {
        if (targetReducer.editingType === 'details')
            await axios.post(`${apiUrl}/settargetinfo`, targetReducer.editingTarget);
        else
            await axios.post(`${apiUrl}/settarget/${formattedDate}`, targetReducer.editingTarget);
    } catch {
        // ignore — matches legacy behaviour
    }

    dispatch(setEditing({ targetId: null, editingType: null }));
    dispatch(getTargets());
};

export const populateTargets = () => async (
    dispatch: AppDispatch,
    getState: () => RootState,
) => {
    dispatch(setLoading(true));

    const { targetReducer } = getState();
    const formattedDate = fmtDate(targetReducer.selectedDate);

    await axios.get(`${apiUrl}/populateTargets/${formattedDate}`);
    dispatch(getTargets());
};

export default targetSlice.reducer;
